import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Eye, EyeOff, Star, StarOff, Search, Music, Video } from 'lucide-react';

const Videos = () => {
  const { API } = useApp();
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchVideos();
  }, [API]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/admin/youtube/videos`, { withCredentials: true });
      setVideos(response.data.videos || []);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const updateOverride = async (videoId, updates) => {
    try {
      await axios.put(`${API}/admin/youtube/video/${videoId}`, {
        video_id: videoId,
        ...updates
      }, { withCredentials: true });
      
      setVideos(prev => prev.map(v => 
        v.video_id === videoId ? { ...v, ...updates } : v
      ));
      toast.success('Video updated');
    } catch (error) {
      toast.error('Failed to update video');
    }
  };

  const toggleHidden = (video) => {
    updateOverride(video.video_id, { hidden: !video.hidden, featured: video.featured });
  };

  const toggleFeatured = (video) => {
    updateOverride(video.video_id, { featured: !video.featured, hidden: video.hidden });
  };

  const updateOrder = (video, order) => {
    const orderValue = order === '' ? null : parseInt(order);
    updateOverride(video.video_id, { order: orderValue, hidden: video.hidden, featured: video.featured });
  };

  const filteredVideos = videos.filter(video => {
    // Type filter
    if (filter === 'releases' && video.playlist_type !== 'releases') return false;
    if (filter === 'music_videos' && video.playlist_type !== 'music_videos') return false;
    if (filter === 'hidden' && !video.hidden) return false;
    if (filter === 'featured' && !video.featured) return false;
    // Search filter
    if (search && !video.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Sort by published_at desc (newest first)
  const sortedVideos = [...filteredVideos].sort((a, b) => {
    const dateA = a.published_at || '';
    const dateB = b.published_at || '';
    return dateB.localeCompare(dateA);
  });

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Count videos by type
  const releasesCount = videos.filter(v => v.playlist_type === 'releases').length;
  const musicVideosCount = videos.filter(v => v.playlist_type === 'music_videos').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-videos">
      <div>
        <h1 className="font-syne font-bold text-2xl text-white">Video Management</h1>
        <p className="text-white/50">Control visibility, featured status, and order of videos</p>
      </div>

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-brand-dark border border-white/5 p-1">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-brand-red data-[state=active]:text-white"
          >
            All ({videos.length})
          </TabsTrigger>
          <TabsTrigger 
            value="releases" 
            className="data-[state=active]:bg-brand-red data-[state=active]:text-white"
          >
            <Music className="w-4 h-4 mr-2" />
            Releases ({releasesCount})
          </TabsTrigger>
          <TabsTrigger 
            value="music_videos" 
            className="data-[state=active]:bg-brand-red data-[state=active]:text-white"
          >
            <Video className="w-4 h-4 mr-2" />
            Videos ({musicVideosCount})
          </TabsTrigger>
          <TabsTrigger 
            value="featured" 
            className="data-[state=active]:bg-brand-gold data-[state=active]:text-white"
          >
            <Star className="w-4 h-4 mr-2" />
            Featured
          </TabsTrigger>
          <TabsTrigger 
            value="hidden" 
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white/70"
          >
            <EyeOff className="w-4 h-4 mr-2" />
            Hidden
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <Card className="bg-brand-dark border-white/5">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              placeholder="Search videos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-brand-charcoal border-white/10 text-white"
              data-testid="video-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Videos Table */}
      <Card className="bg-brand-dark border-white/5">
        <CardContent className="p-0">
          {sortedVideos.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/50">
                {videos.length === 0 
                  ? 'No videos found. Sync from YouTube first.'
                  : 'No videos match your filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-white/50">Video</TableHead>
                    <TableHead className="text-white/50">Type</TableHead>
                    <TableHead className="text-white/50">Published</TableHead>
                    <TableHead className="text-white/50">Duration</TableHead>
                    <TableHead className="text-white/50">Status</TableHead>
                    <TableHead className="text-white/50 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedVideos.map(video => (
                    <TableRow 
                      key={video.video_id} 
                      className="border-white/5 hover:bg-white/5"
                      data-testid={`video-row-${video.video_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img
                            src={video.thumbnail}
                            alt={video.title}
                            className="w-20 h-12 object-cover rounded"
                          />
                          <div className="max-w-xs">
                            <p className="text-white text-sm font-medium line-clamp-1">
                              {video.title}
                            </p>
                            <p className="text-white/40 text-xs font-mono">
                              {video.video_id}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={video.playlist_type === 'releases' 
                            ? 'border-brand-red text-brand-red' 
                            : 'border-brand-gold text-brand-gold'}
                        >
                          {video.playlist_type === 'releases' ? 'Release' : 'Video'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-white/50 text-sm">
                        {formatDate(video.published_at)}
                      </TableCell>
                      <TableCell className="text-white/70 font-mono text-sm">
                        {formatDuration(video.duration)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {video.hidden && (
                            <Badge variant="secondary" className="bg-white/10 text-white/50">
                              Hidden
                            </Badge>
                          )}
                          {video.featured && (
                            <Badge className="bg-brand-gold text-white">
                              Featured
                            </Badge>
                          )}
                          {!video.hidden && !video.featured && (
                            <span className="text-white/30 text-sm">Visible</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleHidden(video)}
                            className={video.hidden ? 'text-white/40 hover:text-white' : 'text-white hover:text-white/70'}
                            title={video.hidden ? 'Show' : 'Hide'}
                          >
                            {video.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleFeatured(video)}
                            className={video.featured ? 'text-brand-gold hover:text-brand-gold/70' : 'text-white/40 hover:text-brand-gold'}
                            title={video.featured ? 'Unfeature' : 'Feature'}
                          >
                            {video.featured ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Videos;
